//import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ConfirmationModal from './ConfirmationModal';
import React, { useEffect, useState } from 'react';
import VectorIcon from '/images/Vector.svg';
import { updateSolutionAPI } from '../../services/commonService';

interface LoaderProps {
  message?: string;
  solutionId?: string;
  isElaboratedSummaryStep?: boolean;
  isOpportunityReport?: boolean;
  notify?: boolean;
  width?: string;
  disableNotifyButton?: boolean;
  fullScreen?: boolean;
}

const Loader: React.FC<LoaderProps> = ({
  message = 'Expanding provided information',
  solutionId,
  isElaboratedSummaryStep = false,
  isOpportunityReport = false,
  notify = false,
  disableNotifyButton,
  fullScreen = true,
}) => {
  const notifyKey = `notifyCompleted_${solutionId}_${isElaboratedSummaryStep ? 'elab' : ''}${isOpportunityReport ? 'oppr' : ''}`;
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [displayMessage, setDisplayMessage] = useState<string>(() => message);
  const [notifyCompleted, setNotifyCompleted] = useState(() => {
    try {
      return localStorage.getItem(notifyKey) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (disableNotifyButton) {
      setDisplayMessage('You will be notified');
    } else {
      setDisplayMessage(message);
    }
  }, [message, disableNotifyButton]);
  const segments = Array.from({ length: 20 }, (_, index) => {
    const rotation = index * 18;
    const opacity = 0.2 + (index / 20) * 0.8;

    return (
      <Box
        key={index}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '3px',
          height: '6px',
          backgroundColor: '#2563EB',
          borderRadius: '2px',
          transformOrigin: '50% 34px',
          transform: `translate(-50%, -100%) rotate(${rotation}deg)`,
          opacity: opacity,
          animation: `fadeRotate 1.2s linear infinite`,
          animationDelay: `${index * 0.06}s`,
          '@keyframes fadeRotate': {
            '0%': { opacity: 0.2 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.2 },
          },
        }}
      />
    );
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: fullScreen ? '100vh' : '100%',
        width: fullScreen ? '100vw' : '100%',
        backgroundColor: fullScreen ? '#FFFFFF' : 'transparent',
        padding: 2,
      }}
    >
      {/* Loader */}
      <Box
        sx={{
          width: '68px',
          height: '68px',
          position: 'relative',
        }}
      >
        {segments}
      </Box>

      {/* Message */}
      <Typography
        sx={{
          mt: 5,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '24px',
          color: '#AEAEAE',
          textAlign: 'center',
          maxWidth: '82%',
          whiteSpace: 'pre-line',
        }}
      >
        {displayMessage}
      </Typography>
      {notify && (
        <>
          <Button
            variant="outlined"
            size="small"
            disabled={disableNotifyButton || notifyCompleted}
            onClick={() => {
              console.log('Notify Me clicked');

              setIsNotifyModalOpen(true);
            }}
            title={
              notifyCompleted
                ? 'You will be notified soon when summary has been generated via email.'
                : undefined
            }
            sx={{
              marginTop: 2,
              marginLeft: 1,
              borderColor: '#1B54F8',
              backgroundColor: '#1B54F8',
              color: '#ffffff',
              fontFamily: 'Poppins',
              fontSize: '14px',
              height: '32px',
              borderRadius: '5.33px',
              textTransform: 'none',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&.Mui-disabled': {
                backgroundColor: '#A4BBFC',
                borderColor: '#A4BBFC',
                color: '#ffffff',
              },
            }}
          >
            <img src={VectorIcon} alt="vector" width={22} height={22} style={{ marginRight: 4 }} />
            Notify Me
          </Button>
          <ConfirmationModal
            isOpen={isNotifyModalOpen}
            title="Notify me"
            description={
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '16px',
                    letterSpacing: '0px',
                    color: '#333',
                  }}
                >
                  {isOpportunityReport
                    ? `This process typically takes around 30-45 minutes. You will be notified on your registered Email ID once the report is ready.`
                    : `This process typically takes around 10-15 minutes. You will be notified on your registered Email ID once the report is ready.`}{' '}
                </Typography>
              </Box>
            }
            primaryButtonText={<span>{notifyLoading ? 'Sending...' : 'Continue'}</span>}
            secondaryButtonText={<span>Cancel</span>}
            onPrimaryAction={async () => {
              if ((isElaboratedSummaryStep || isOpportunityReport) && solutionId) {
                setNotifyLoading(true);
                try {
                  // Prepare the payload based on the report type
                  const payload: {
                    notify_for_elaborate_file?: boolean;
                    notify_for_opportunity?: boolean;
                  } = {};
                  if (isElaboratedSummaryStep) {
                    payload.notify_for_elaborate_file = true;
                  }

                  if (isOpportunityReport) {
                    payload.notify_for_opportunity = true;
                  }

                  await updateSolutionAPI(solutionId, payload);

                  if (payload.notify_for_elaborate_file || payload.notify_for_opportunity) {
                    setDisplayMessage('You will be notified');
                  }
                  setNotifyCompleted(true);
                  try {
                    localStorage.setItem(notifyKey, 'true');
                  } catch {}
                } catch (e) {
                  // alert('Failed to send notify PATCH: ' + (e instanceof Error ? e.message : JSON.stringify(e)));
                  console.log('Failed to send notify PATCH:', e);
                } finally {
                  setIsNotifyModalOpen(false);
                  setNotifyLoading(false);
                }
              } else {
                setIsNotifyModalOpen(false);
              }
            }}
            onClose={() => setIsNotifyModalOpen(false)}
            onSecondaryAction={() => setIsNotifyModalOpen(false)}
            sx={{
              '& .MuiDialog-paper': {
                top: '30%',
                transform: 'translate(0, -50%)',
                position: 'absolute',
              },
            }}
          />
        </>
      )}
    </Box>
  );
};

export default Loader;
